<script setup lang="ts">
import { type PropType } from 'vue';

import { AuctionState, SellingMethod, AssetType } from '~/apiClient';
import type { BasePaginationProps } from '~/components/Base/BasePagination.vue';
import { type SaleListingItemEntity } from '~/types/sale.type';

// PROPS
const { isLoading, sales } = defineProps({
    isLoading: { type: Boolean, required: true },
    sales: { type: Array as PropType<SaleListingItemEntity[]>, default: () => [] },
    pagination: { type: Object as PropType<Omit<BasePaginationProps, 'disabled'>>, required: true },
});

// COMPOSABLES
const { appliedFilters, appliedFiltersCounter } = useSlpData();
const { navigationItems } = useSlpNavigationItems();
const { tt } = useTypedI18n();
const { setTrackingReferralListName, trackSlpSelectSale } = useTracking();
const { salesContact, suspense: suspenseSalesContact } = useSalesContact({ appliedFilters });
const sellingMethod = useSlpSellingMethod();
const { reset, aState } = useSlpParams(sellingMethod);
const { formatSlpRoute } = useSlpRouteUtils();

await suspenseSalesContact();

// DATA
const salesContactFormModalState = useModalState<boolean>();

const isOnlyRunningAuctionAppliedFilter = computed(
    () => sellingMethod.value === SellingMethod.Auction && appliedFiltersCounter.value === 1 && aState.value[0] === AuctionState.Running
);

// HANDLERS
const onSlpCardNavigate = (): void => {
    trackSlpSelectSale();
    setTrackingReferralListName('page');
};
</script>

<template>
    <BaseWidthContainer as="section" class="relative pb-6 lg:grid lg:grid-cols-4 lg:gap-8">
        <aside class="mr-6 hidden pt-7.5 lg:block">
            <div class="relative flex flex-col gap-y-6">
                <SlpFiltersAdvancedModalButton variant="expanded" />
                <SlpFilters />
            </div>
        </aside>
        <div class="flex flex-col gap-y-4 pt-5 md:pt-6 lg:col-span-3">
            <div class="sticky top-[var(--header-total-height)] z-1 -mr-1 -ml-1 bg-white py-1">
                <SlpNavigation :items="navigationItems" @open-contact-form="salesContactFormModalState.open()" />
            </div>
            <div class="space-y-4">
                <SlpResults :total="pagination.total" />
                <div class="flex flex-row gap-2">
                    <SlpAppliedFilter />
                </div>
                <SlpSorting />
            </div>

            <SlpListGrid :sales="sales" :is-loading="isLoading" :pagination="pagination" show-trust-report-banner>
                <template #empty-state>
                    <div class="my-24 flex aspect-4/3 w-full flex-col items-center justify-center gap-3 text-center md:aspect-8/3 xl:aspect-12/3">
                        <SvgoSteeringWheel class="h-16" filled :font-controlled="false" />

                        <template v-if="isOnlyRunningAuctionAppliedFilter">
                            <p class="text-dimmed text-2xl font-semibold">
                                {{ tt('slp.Auction.page.emptyStateTitleRunningFilterOnly') }}
                            </p>
                            <BaseI18nT tag="p" keypath="slp.Auction.page.emptyStateTextRunningFilterOnly" class="md:text-lg">
                                <NuxtLinkLocale
                                    class="text-primary hover:underline"
                                    :to="formatSlpRoute({ sellingMethod: SellingMethod.DirectSale, assetType: AssetType.Bus })"
                                >
                                    {{ tt('menu.directSale') }}
                                </NuxtLinkLocale>
                            </BaseI18nT>
                        </template>
                        <template v-else>
                            <p class="text-dimmed text-2xl font-semibold">
                                {{ tt(`slp.${sellingMethod}.page.emptyStateTitle`) }}
                            </p>
                            <p class="md:text-lg">
                                {{ tt(`slp.${sellingMethod}.page.emptyStateText`) }}
                            </p>
                        </template>

                        <SalesContactCard v-if="salesContact" :sales-contact="salesContact" @open-contact-form="salesContactFormModalState.open()" />
                        <UButton variant="outline" size="xl" color="neutral" :label="tt(`slp.page.resetFilters`)" @click="reset" />
                    </div>
                </template>
                <template #skeleton>
                    <SlpCardSkeleton />
                </template>
                <template #default="{ sale }">
                    <SlpCard :sale="sale" @navigate="onSlpCardNavigate" />
                </template>
            </SlpListGrid>
            <SlpBrandDescription />
        </div>
    </BaseWidthContainer>
    <SalesContactFormModal
        v-if="salesContact"
        v-model="salesContactFormModalState.isOpen"
        :sales-contact="salesContact"
        @success="salesContactFormModalState.confirm(true)"
    />
</template>
